// src/services/billing.service.ts
import { db } from '../config/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  Timestamp, 
  doc, 
  getDoc,
  addDoc
} from 'firebase/firestore';
import { 
  BillingPeriod, 
  BillingLead, 
  BillingSummary,
  BillingSnapshot,
  DeterminationChange
} from '../types/billing.types';
import { Lead } from '../types/lead.types';
import { startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import { getUserById } from './user.service';
import { billingAdjustmentsService } from './billingAdjustments.service';

export const billingService = {
  async getBillingForPeriod(
    projectId: string,
    start: Date,
    end: Date
  ): Promise<BillingPeriod> {
    try {
      // Get project name and billing rate
      const projectDoc = await getDoc(doc(db, 'projects', projectId));
      const projectData = projectDoc.data();
      const projectName = projectData?.name || 'Unknown Project';
      const billRate = projectData?.settings?.billingInfo?.ratePerValidLead || 45; // Default $45 if not configured

      // Get leads created in this period
      const leadsQuery = query(
        collection(db, 'leads'),
        where('projectId', '==', projectId),
        where('createdAt', '>=', Timestamp.fromDate(start)),
        where('createdAt', '<=', Timestamp.fromDate(end))
      );

      const snapshot = await getDocs(leadsQuery);
      const leads = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lead));

      // Transform leads to billing format
      const billingLeads = await Promise.all(
        leads.map(async (lead) => {
          const capturedByName = lead.capturedByName || 
            (lead.capturedBy ? await this.getUserName(lead.capturedBy) : 'Unknown');
          
          const children = (lead.children || []).map(child => ({
            name: child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim(),
            grade: child.grade || '',
            dateOfBirth: child.dateOfBirth || ''
          }));

          return {
            leadId: lead.id,
            parentName: lead.parentName || 'Unknown',
            children,
            capturedBy: lead.capturedBy || 'unknown',
            capturedByName,
            capturedDate: lead.createdAt.toDate(),
            currentDetermination: lead.determination || 'pending',
            determinationHistory: lead.determinationHistory || [],
            enrolled: lead.status === 'enrolled',
            enrolledStudents: lead.status === 'enrolled' ? children.length : 0,
            status: lead.status,
            billableAmount: children.length * billRate
          } as BillingLead;
        })
      );

      // Get determination changes for this period
      const adjustments = await billingAdjustmentsService.getBillingPeriodAdjustments(
        projectId,
        start,
        end
      );

      // Calculate summary
      const summary = this.calculateBillingSummary(billingLeads, adjustments.adjustments);

      return {
        projectId,
        projectName,
        periodStart: start,
        periodEnd: end,
        leads: billingLeads,
        summary,
        adjustments: adjustments.adjustments
      };
    } catch (error) {
      console.error('Error getting billing for period:', error);
      throw error;
    }
  },

  async getUserName(userId: string): Promise<string> {
    try {
      const user = await getUserById(userId);
      return user?.name || user?.email || 'Unknown';
    } catch (error) {
      console.error('Error fetching user name:', error);
      return 'Unknown';
    }
  },

  calculateBillingSummary(
    leads: BillingLead[], 
    adjustments: DeterminationChange[] = []
  ): BillingSummary {
    let totalLeads = leads.length;
    let totalChildren = 0;
    let validLeads = 0;
    let invalidLeads = 0;
    let pendingLeads = 0;
    let validStudents = 0;
    let enrolledStudents = 0;

    leads.forEach(lead => {
      const childCount = lead.children.length;
      totalChildren += childCount;

      if (lead.currentDetermination === 'valid') {
        validLeads++;
        validStudents += childCount;
        if (lead.enrolled) {
          enrolledStudents += lead.enrolledStudents || 0;
        }
      } else if (lead.currentDetermination === 'invalid') {
        invalidLeads++;
      } else {
        pendingLeads++;
      }
    });

    const conversionRate = validStudents > 0 
      ? (enrolledStudents / validStudents) * 100 
      : 0;

    return {
      totalLeads,
      totalParents: leads.length,
      totalChildren,
      validLeads,
      invalidLeads,
      pendingLeads,
      validStudents,
      enrolledStudents,
      totalBillable: validStudents,
      totalBilled: 0,
      overThresholdLeads: 0,
      determinationAdjustments: adjustments.length,
      conversionRate
    };
  },

  async calculateWeeklyBilling(
    projectId: string,
    weekStart: Date
  ): Promise<BillingPeriod> {
    const start = startOfWeek(weekStart, { weekStartsOn: 0 });
    const end = endOfWeek(weekStart, { weekStartsOn: 0 });
    
    const billing = await this.getBillingForPeriod(projectId, start, end);
    
    // Get project name
    const projectDoc = await getDoc(doc(db, 'projects', projectId));
    const projectData = projectDoc.data();
    
    // Add project name if needed
    if (projectData?.name) {
      billing.projectName = projectData.name;
    }
    
    return billing;
  },

  async getProjectBillingHistory(
    projectId: string,
    weeks: number = 12
  ): Promise<BillingPeriod[]> {
    const history: BillingPeriod[] = [];
    const today = new Date();
    
    for (let i = 0; i < weeks; i++) {
      const weekStart = subWeeks(startOfWeek(today, { weekStartsOn: 0 }), i);
      const billing = await this.calculateWeeklyBilling(projectId, weekStart);
      history.push(billing);
    }
    
    return history;
  },

  async createBillingSnapshot(billing: BillingPeriod): Promise<void> {
    try {
      const snapshot: Partial<BillingSnapshot> = {
        projectId: billing.projectId,
        weekStart: billing.periodStart,
        weekEnd: billing.periodEnd,
        leads: billing.leads.map(lead => ({
          leadId: lead.leadId,
          parentName: lead.parentName,
          children: lead.children,
          capturedBy: lead.capturedBy,
          capturedByName: lead.capturedByName,
          capturedDate: lead.capturedDate,
          determinationAtSnapshot: lead.currentDetermination,
          currentDetermination: lead.currentDetermination,
          enrolled: lead.enrolled || false,
          enrolledStudents: lead.enrolledStudents || 0,
          status: lead.status
        })),
        summary: billing.summary,
        status: 'active',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        lockedAt: Timestamp.now()
      };
      
      await addDoc(collection(db, 'billingSnapshots'), snapshot);
    } catch (error) {
      console.error('Error creating billing snapshot:', error);
      throw error;
    }
  }
};